import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invoke } from '@tauri-apps/api/core';
import { ShieldAlert } from 'lucide-react';
import { Modal } from './ui/Modal';
import VaultPassphraseInput from './VaultPassphraseInput';
import { UNLOCK_PASSPHRASE_ERROR, UNLOCK_RETRY_DELAY_MS } from './vaultPassphraseCopy';
import { vaultErrorToken, refreshVaultMaster } from '../store/vaultStore';
import './Vault.css';

/**
 * The dead-end screen, and the only way out of it.
 *
 * Reached when `vault_master_status` reports `keyMissing`: the vault file says
 * it has no passphrase while this computer's keychain says it does — a file
 * restored from a backup, a cloud-sync conflict resolved the wrong way, a
 * keychain that was reset out from under a vault full of ciphertext. The
 * passphrase and the recovery phrase the user holds are both *correct* and
 * neither can help, because this particular file has no wraps to open.
 *
 * Two rules here are the whole point of the screen:
 *
 * 1. **It must never be the setup modal.** Before this existed the gate showed
 *    "Protect this vault with a passphrase" for exactly this state, whose first
 *    call fails with `vault_locked` and whose message is "That did not work.
 *    Try again." Someone holding both correct secrets was shown the wrong
 *    screen and given a meaningless error.
 * 2. **`vault.json.bak` is named out loud.** The app has written it on every
 *    single save since the vault shipped and read it back never; every recovery
 *    story ended at a valid vault one rename away, in a directory a
 *    non-technical user on macOS cannot reach without Finder's Go-to-Folder.
 *
 * The swap itself is Rust's: `vault_restore_backup` proves the supplied secret
 * opens the backup **before** anything moves, and moves the current file aside
 * rather than deleting it. A backup that cannot be opened is worth less than
 * the file already in place.
 */
export default function VaultBackupRestoreModal({
  isOpen,
  onClose,
  onRestored,
  /** From `vault_master_status`. Drives whether the restore is even offered. */
  backupAvailable = false,
}) {
  const { t } = useTranslation();
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooling, setCooling] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setSecret('');
    setBusy(false);
    setCooling(false);
    setError('');
  }, [isOpen]);

  const errorText = useCallback(
    (e) => {
      switch (vaultErrorToken(e)) {
        case 'vault_no_backup':
          return t(
            'vault.restoreNoBackup',
            'There is no backup copy beside your vault file.'
          );
        case 'vault_key_missing':
          return t(
            'vault.restoreBackupUnopenable',
            'The backup copy cannot be opened on this computer either.'
          );
        // Card 1.1: refused before any secret is tried, so this says nothing
        // about the passphrase.
        case 'vault_update_required':
          return t(
            'vault.updateRequiredBlocked',
            'This vault was saved by a newer version of Apiboo. Update Apiboo first — nothing was changed.'
          );
        // Same fixed string as every other pane: no screen says which factor
        // failed or how close it was.
        default:
          return t('vault.unlockFailed', UNLOCK_PASSPHRASE_ERROR);
      }
    },
    [t]
  );

  const restore = async () => {
    if (busy || cooling) return;
    setBusy(true);
    setError('');
    try {
      await invoke('vault_restore_backup', { secret });
      setSecret('');
      await refreshVaultMaster();
      if (onRestored) onRestored();
    } catch (e) {
      setError(errorText(e));
      setCooling(true);
      setTimeout(() => setCooling(false), UNLOCK_RETRY_DELAY_MS);
    }
    setBusy(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={busy ? () => {} : onClose}
      preventBackdropClose
      size="xl"
      title={t('vault.keyMissingTitle', 'This vault does not match this computer')}
    >
      <div className="vault-pass-body">
        <div className="vault-pass-warn">
          <span className="vault-pass-warn-title">
            <ShieldAlert size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
            {t('vault.keyMissingHeadline', 'Your passphrase is not the problem.')}
          </span>
          <span className="vault-pass-warn-text">
            {t(
              'vault.keyMissingBody',
              'The vault file on this computer is older than the key Apiboo has for it, so nothing can open it — not your passphrase and not your recovery phrase. This usually means the file was restored from a backup, or copied here from another machine.'
            )}
          </span>
        </div>

        <p className="vault-pass-text">
          {t(
            'vault.keyMissingWhereBody',
            'Apiboo keeps a copy of the previous file next to it, named vault.json.bak, in the app data folder. Do not delete either file.'
          )}
        </p>

        {backupAvailable ? (
          <>
            <p className="vault-pass-text">
              {t(
                'vault.restoreIntro',
                'That backup copy is here. Enter your passphrase or your 12-word recovery phrase and Apiboo will check that it opens the backup before swapping it in. The current file is kept, renamed, not deleted.'
              )}
            </p>
            <VaultPassphraseInput
              id="vault-restore-secret"
              label={t('vault.restoreField', 'Passphrase or recovery phrase')}
              value={secret}
              onChange={setSecret}
              autoComplete="current-password"
              autoFocus
              disabled={busy}
              onKeyDown={(e) => {
                if (e.key === 'Enter') restore();
              }}
            />
          </>
        ) : (
          <p className="vault-pass-text">
            {t(
              'vault.restoreNoBackupBody',
              'There is no backup copy beside the vault file on this computer. If you have a copy of the vault folder from before, restore the whole folder and open Apiboo again.'
            )}
          </p>
        )}

        {error && <div className="vault-modal-error">{error}</div>}

        <div className="modal-footer-row">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {t('common.close')}
          </button>
          {backupAvailable && (
            <button
              type="button"
              onClick={restore}
              disabled={busy || cooling || !secret}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
            >
              {busy
                ? t('vault.restoreBusy', 'Checking…')
                : t('vault.restorePrimary', 'Use the backup copy')}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
